import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type { ReportSummary } from '../types';

export function useReportSummary(from: string, to: string) {
  return useQuery({
    queryKey: qk.reports.summary(from, to),
    queryFn: () => api.get<ReportSummary>('/reports/summary', { from, to }),
    staleTime: 5 * 60_000,
  });
}

/** ─── Справки по курсове, клиенти, шофьори, бусове ───────────────────── */

export interface TripsReportRow {
  tripId: string;
  number: string;
  status: string;
  startAt: string | null;
  endAt: string | null;
  vehicleId: string | null;
  vehicle: string | null;
  driverId: string | null;
  driver: string | null;
  from: string | null;
  to: string | null;
  orders: number;
  clients: string | null;
  weightKg: number | null;
  loadPct: number | null;
  plannedKm: number | null;
  km: number | null;
  emptyKm: number | null;
  revenueMinor: number;
  fuelMinor: number | null;
  tollsMinor: number | null;
  otherMinor: number | null;
  payMinor: number | null;
  marginMinor: number | null;
  docsComplete: number;
}

export interface TripsReport {
  from: string;
  to: string;
  ccy: string;
  seesMargin: boolean;
  seesPay: boolean;
  rows: TripsReportRow[];
  totals: {
    trips: number;
    orders: number;
    km: number;
    emptyKm: number;
    revenueMinor: number;
    fuelMinor: number | null;
    tollsMinor: number | null;
    otherMinor: number | null;
    payMinor: number | null;
    marginMinor: number | null;
    ordersDocsComplete: number;
  };
}

export interface CustomersReportRow {
  partnerId: string;
  name: string;
  country: string | null;
  orders: number;
  delivered: number;
  cancelled: number;
  weightKg: number;
  revenueMinor: number;
  km: number;
  revenuePerKmMinor: number | null;
  marginMinor: number | null;
  onTimePct: number | null;
  deliveriesMeasured: number;
  outstandingMinor: number | null;
  overdueMinor: number | null;
  avgDaysToPay: number | null;
  lastOrderAt: string | null;
}

export interface CustomersReport {
  from: string;
  to: string;
  ccy: string;
  seesMargin: boolean;
  seesInvoices: boolean;
  rows: CustomersReportRow[];
  totals: {
    clients: number;
    orders: number;
    revenueMinor: number;
    km: number;
    marginMinor: number | null;
    outstandingMinor: number | null;
    overdueMinor: number | null;
  };
}

export interface DriversReportRow {
  driverId: string;
  name: string;
  status: string;
  trips: number;
  km: number | null;
  days: number;
  deliveries: number;
  lateDeliveries: number;
  incidents: number;
  accidents: number;
  returnedDocuments: number;
  payMinor: number | null;
  rateMissing: boolean;
}

export interface DriversReport {
  from: string;
  to: string;
  ccy: string;
  seesPay: boolean;
  rows: DriversReportRow[];
  totals: {
    trips: number;
    km: number;
    days: number;
    deliveries: number;
    lateDeliveries: number;
    incidents: number;
    payMinor: number | null;
  };
}

export interface VansReportRow {
  vehicleId: string;
  fleetNo: string;
  plate: string;
  status: string;
  trips: number;
  km: number | null;
  emptyKm: number;
  emptyPct: number | null;
  daysUsed: number;
  utilisationPct: number;
  revenueMinor: number;
  revenuePerKmMinor: number | null;
  litres: number | null;
  litresPer100: number | null;
  fuelCostMinor: number | null;
  costMinor: number | null;
  costPerKmMinor: number | null;
  marginMinor: number | null;
  repairs: number;
}

export interface VansReport {
  from: string;
  to: string;
  ccy: string;
  seesMargin: boolean;
  rows: VansReportRow[];
  totals: {
    trips: number;
    km: number;
    emptyKm: number;
    revenueMinor: number;
    litres: number;
    costMinor: number | null;
    marginMinor: number | null;
  };
}

export function useTripsReport(from: string, to: string, filters: { vehicleId?: string; driverId?: string; partnerId?: string }) {
  const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => Boolean(v))) as Record<string, string>;
  return useQuery({
    queryKey: ['reports', 'trips', from, to, clean],
    queryFn: () => api.get<TripsReport>('/reports/trips', { from, to, ...clean }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useCustomersReport(from: string, to: string) {
  return useQuery({
    queryKey: ['reports', 'customers', from, to],
    queryFn: () => api.get<CustomersReport>('/reports/customers', { from, to }),
    staleTime: 60_000,
  });
}

export function useDriversReport(from: string, to: string) {
  return useQuery({
    queryKey: ['reports', 'drivers', from, to],
    queryFn: () => api.get<DriversReport>('/reports/drivers', { from, to }),
    staleTime: 60_000,
  });
}

export function useVansReport(from: string, to: string) {
  return useQuery({
    queryKey: ['reports', 'vans', from, to],
    queryFn: () => api.get<VansReport>('/reports/vans', { from, to }),
    staleTime: 60_000,
  });
}
